import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { FlaskConical, Beaker, Star, MessagesSquare, Activity, CalendarClock, AlertTriangle, TrendingDown, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { inr, fmtDate, fmtDateTime, label } from "@/lib/constants";

const STAGE_COLORS = { planned: "#C9B8A3", in_progress: "#C5A059", completed: "#4A2E1F", cancelled: "#D8CBB8" };

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard/summary")).data,
  });

  if (isLoading || !data) return <div className="text-[#8C7A6B] text-sm" data-testid="dashboard-loading">Loading dashboard…</div>;

  const stageData = Object.entries(data.stage_counts).map(([k, v]) => ({ stage: label(k), key: k, count: v }));

  const stats = [
    { label: "Active Experiments", value: data.active_experiments, icon: FlaskConical, testid: "stat-active-experiments" },
    { label: "Formulas on Record", value: data.formulas_count, icon: Beaker, testid: "stat-formulas" },
    { label: "Avg Sensory Liking", value: data.avg_sensory_score, icon: Star, testid: "stat-avg-sensory" },
    { label: "Feedback Entries", value: data.feedback_count, icon: MessagesSquare, testid: "stat-feedback-count" },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-8 pb-4 border-b border-[#E8DFD1]">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#1C100B]">Dashboard</h1>
          <p className="text-sm text-[#8C7A6B] mt-1">R&D pipeline at a glance.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map(({ label: l, value, icon: Icon, testid }) => (
          <div key={l} className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid={testid}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold tracking-wider uppercase text-[#8C7A6B]">{l}</span>
              <Icon size={18} className="text-[#C5A059]" />
            </div>
            <div className="mt-3 font-display text-4xl font-bold text-[#2A1810]">{value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm lg:col-span-2" data-testid="stage-chart-card">
          <h2 className="text-lg font-semibold text-[#1C100B] mb-4 font-display">Experiments by Stage</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stageData}>
              <XAxis dataKey="stage" tick={{ fontSize: 12, fill: "#8C7A6B" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#8C7A6B" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#FAF6F0" }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {stageData.map((d) => (
                  <Cell key={d.key} fill={STAGE_COLORS[d.key]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="margin-snapshot-card">
          <h2 className="text-lg font-semibold text-[#1C100B] mb-4 font-display">Margin Snapshot</h2>
          <div className="space-y-3">
            {data.top_margins.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2" data-testid={`margin-row-${c.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#2A1810] truncate">{c.formula_name}</div>
                  <div className="text-xs text-[#8C7A6B]">{label(c.channel)} · {inr(c.proposed_retail_price)}</div>
                </div>
                <span className="text-sm font-bold text-[#15803D] bg-[#F0FDF4] px-2 py-0.5 rounded-full shrink-0">
                  {c.contribution_margin_percent}%
                </span>
              </div>
            ))}
          </div>
          <Link to="/packaging" className="block mt-4 text-xs font-semibold text-[#B08B46] hover:underline" data-testid="margin-view-all">
            View all costing scenarios →
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="drift-alerts-card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingDown size={17} className="text-[#B91C1C]" />
            <h2 className="text-lg font-semibold text-[#1C100B] font-display">Shelf-Life Drift Alerts</h2>
          </div>
          <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
            {(data.drift_alerts || []).map((d) => (
              <Link
                key={d.id}
                to={`/experiments/${d.experiment_id}`}
                className="flex items-center justify-between gap-3 border-b border-[#F4EFEA] last:border-0 pb-3 last:pb-0 hover:bg-[#FDFBF7] -mx-2 px-2 rounded transition-colors"
                data-testid={`drift-alert-${d.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#2A1810] truncate">{d.experiment_name}</div>
                  <div className="text-xs text-[#8C7A6B]">
                    D{d.from_day} → D{d.to_day} · {d.storage_condition} · {d.from_score} → {d.to_score}/10
                  </div>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border shrink-0 bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]">
                  −{d.drop} pts
                </span>
              </Link>
            ))}
            {!(data.drift_alerts || []).length && (
              <p className="text-sm text-[#8C7A6B]" data-testid="drift-alerts-empty">
                No sharp quality drops between checkpoints. Shelf-life curves are holding steady.
              </p>
            )}
          </div>
        </div>

        <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="cost-watch-card">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={17} className="text-[#92400E]" />
            <h2 className="text-lg font-semibold text-[#1C100B] font-display">Cost Watch</h2>
          </div>
          <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
            {(data.cost_watch?.items || []).map((c) => (
              <Link
                key={c.id}
                to={`/formulas/${c.id}`}
                className="flex items-center justify-between gap-3 border-b border-[#F4EFEA] last:border-0 pb-3 last:pb-0 hover:bg-[#FDFBF7] -mx-2 px-2 rounded transition-colors"
                data-testid={`cost-watch-${c.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#2A1810] truncate">{c.name}</div>
                  <div className="text-xs text-[#8C7A6B]">
                    {inr(c.baseline_cost_per_kg)} → {inr(c.current_cost_per_kg)}/kg since v{c.baseline_version} (approved)
                  </div>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border shrink-0 bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]">
                  +{c.rise_percent}%
                </span>
              </Link>
            ))}
            {!(data.cost_watch?.items || []).length && (
              <p className="text-sm text-[#8C7A6B]" data-testid="cost-watch-empty">
                No formula has risen more than {data.cost_watch?.threshold_percent ?? 5}% above its last approved cost.
              </p>
            )}
          </div>
        </div>

        <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="stability-due-card">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock size={17} className="text-[#C5A059]" />
            <h2 className="text-lg font-semibold text-[#1C100B] font-display">Upcoming & Overdue Stability Checks</h2>
          </div>
          <div className="space-y-3">
            {(data.stability_due || []).map((c) => (
              <Link
                key={c.id}
                to={`/experiments/${c.experiment_id}`}
                className="flex items-center justify-between gap-3 border-b border-[#F4EFEA] last:border-0 pb-3 last:pb-0 hover:bg-[#FDFBF7] -mx-2 px-2 rounded transition-colors"
                data-testid={`stability-due-${c.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#2A1810] truncate">{c.experiment_name}</div>
                  <div className="text-xs text-[#8C7A6B]">Day {c.day} · {c.storage_condition || "default condition"} · due {fmtDate(c.due_date)}</div>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border shrink-0 ${
                  c.overdue ? "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]" : "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]"
                }`}>
                  {c.overdue ? <><AlertTriangle size={10} className="inline mr-1 -mt-0.5" />{Math.abs(c.days_until)}d overdue</> : `in ${c.days_until}d`}
                </span>
              </Link>
            ))}
            {!(data.stability_due || []).length && (
              <p className="text-sm text-[#8C7A6B]" data-testid="stability-due-empty">No stability checkpoints due in the next 7 days.</p>
            )}
          </div>
        </div>

        <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="activity-card">
          <div className="flex items-center gap-2 mb-4">
            <Activity size={17} className="text-[#C5A059]" />
            <h2 className="text-lg font-semibold text-[#1C100B] font-display">Recent Activity</h2>
          </div>
          <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
            {(data.recent_activity || []).map((a) => (
              <div key={a.id} className="flex items-start gap-3 border-b border-[#F4EFEA] last:border-0 pb-3 last:pb-0" data-testid={`activity-row-${a.id}`}>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#F4EFEA] text-[#6B584B] border border-[#D8CBB8] px-1.5 py-0.5 rounded shrink-0 mt-0.5">
                  {a.action}
                </span>
                <div className="min-w-0">
                  <div className="text-sm text-[#2A1810] leading-snug">
                    <span className="font-medium">{a.user_name || "—"}</span>{" "}
                    <span className="text-[#5C4A3E]">{a.summary || `${a.action} ${a.entity_type}`}</span>
                  </div>
                  <div className="text-xs text-[#8C7A6B] mt-0.5">{label(a.entity_type)} · {fmtDateTime(a.created_at)}</div>
                </div>
              </div>
            ))}
            {!(data.recent_activity || []).length && (
              <p className="text-sm text-[#8C7A6B]" data-testid="activity-empty">No activity recorded yet.</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="top-sensory-card">
          <h2 className="text-lg font-semibold text-[#1C100B] mb-4 font-display">Top Sensory Results</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[#8C7A6B] border-b border-[#F4EFEA]">
                <th className="pb-2 font-semibold">Experiment</th>
                <th className="pb-2 font-semibold">Panel</th>
                <th className="pb-2 font-semibold text-right">Liking</th>
              </tr>
            </thead>
            <tbody>
              {data.top_sensory.map((s) => (
                <tr key={s.id} className="border-b border-[#F4EFEA] last:border-0" data-testid={`sensory-row-${s.id}`}>
                  <td className="py-2.5 text-[#2A1810] font-medium pr-2">{s.experiment_name}</td>
                  <td className="py-2.5 text-[#8C7A6B]">{label(s.panel_type)}</td>
                  <td className="py-2.5 text-right font-bold text-[#B08B46]">{s.overall_liking_score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="feedback-highlights-card">
          <h2 className="text-lg font-semibold text-[#1C100B] mb-4 font-display">Experience Centre Highlights</h2>
          <div className="space-y-4">
            {data.recent_feedback.map((f) => (
              <div key={f.id} className="border-b border-[#F4EFEA] last:border-0 pb-3 last:pb-0" data-testid={`feedback-row-${f.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <StatusBadge value={f.source} />
                  {f.rating != null && <span className="text-xs font-bold text-[#B08B46]">★ {f.rating}/5</span>}
                  <span className="text-xs text-[#8C7A6B] ml-auto">{fmtDate(f.recorded_at)}</span>
                </div>
                <p className="text-sm text-[#5C4A3E] leading-relaxed line-clamp-2">{f.comment}</p>
                <div className="text-xs text-[#8C7A6B] mt-1">{f.experiment_name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
